import { useState, type CSSProperties, type ReactNode } from "react";
import clsx from "clsx";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import { Table, type Column } from "@/components/ui/Table";
import { EmptyState } from "@/components/ui/EmptyState";
import { MaintenanceProgressBar } from "../components/MaintenanceProgressBar";
import { getGradeColor } from "../form/runtimeModel";
import {
  EVALUATION_STATUS_LABELS,
  formatDateTime,
  formatNumber,
  type AssetEvaluationRow,
  type DefectGroup,
  type EvaluationModel,
  type EvaluationQuestion,
  type EvaluationStatusFilter,
  type GradeBucket,
  type MaterialAggregate,
  type QuestionBreakdown,
} from "./evaluationModel";
import "./auswertung.css";

/* ───────────────────────── Panel ───────────────────────── */

export function Panel({
  title,
  hint,
  actions,
  children,
  className,
}: {
  title: ReactNode;
  hint?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={clsx("wa-panel", className)}>
      <div className="wa-panel__head">
        <div className="min-w-0">
          <h3 className="wa-panel__title">{title}</h3>
          {hint ? <p className="wa-panel__hint">{hint}</p> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      <div className="wa-panel__body">{children}</div>
    </section>
  );
}

/* ───────────────────────── Hero + KPIs ───────────────────────── */

export function EvaluationHero({
  model,
  statusFilter,
  onStatusFilterChange,
  objectLabel,
}: {
  model: EvaluationModel;
  statusFilter: EvaluationStatusFilter;
  onStatusFilterChange: (next: EvaluationStatusFilter) => void;
  objectLabel: string;
}) {
  const s = model.summary;
  const complete = s.totalAssets > 0 && s.done >= s.totalAssets;

  function toggle(next: EvaluationStatusFilter) {
    onStatusFilterChange(statusFilter === next ? "all" : next);
  }

  return (
    <section className={clsx("wa-hero", complete && "wa-hero__complete")} aria-label="Wartungsfortschritt">
      <div>
        <div className="wa-hero__count">
          <span className="wa-hero__value">{s.done}</span>
          <span className="wa-hero__of">von {s.totalAssets}</span>
        </div>
        <p className="wa-hero__caption">
          {objectLabel} gewartet · <span className="wa-hero__percent">{s.percentDone} %</span>
          {s.inProgress > 0 ? ` · ${s.inProgress} in Bearbeitung` : ""}
          {s.open > 0 ? ` · ${s.open} offen` : ""}
        </p>
        <MaintenanceProgressBar
          completed={s.done}
          total={s.totalAssets}
          countLabel={complete ? "Alle Objekte gewartet" : `${s.totalAssets - s.done} Objekt${s.totalAssets - s.done === 1 ? "" : "e"} ausstehend`}
          className="mt-3 max-w-xl"
        />
      </div>
      <div className="wa-kpis" role="group" aria-label="Kennzahlen">
        <Kpi label="Gewartet" value={s.done} tone="ok" active={statusFilter === "done"} onClick={() => toggle("done")} />
        <Kpi
          label="Mit Mängeln"
          value={s.withIssues}
          tone="issue"
          active={statusFilter === "issues"}
          onClick={() => toggle("issues")}
        />
        <Kpi label="Ausstehend" value={s.open + s.inProgress} tone="progress" active={statusFilter === "open"} onClick={() => toggle("open")} />
        <Kpi label="Mängel gesamt" value={s.defectEntries} tone="issue" />
        <Kpi label="Materialposten" value={s.materialEntries} tone="material" />
        <Kpi
          label="Ø Note"
          value={s.averageGrade != null ? formatNumber(Math.round(s.averageGrade * 10) / 10) : "–"}
          tone="neutral"
          style={s.averageGrade != null ? { ["--wa-grade-color" as string]: getGradeColor(Math.round(s.averageGrade)) } : undefined}
        />
      </div>
    </section>
  );
}

function Kpi({
  label,
  value,
  tone,
  active,
  onClick,
  style,
}: {
  label: string;
  value: number | string;
  tone: "neutral" | "ok" | "issue" | "progress" | "material";
  active?: boolean;
  onClick?: () => void;
  style?: CSSProperties;
}) {
  const content = (
    <>
      <span className="wa-kpi__value">{value}</span>
      <span className="wa-kpi__label">{label}</span>
    </>
  );
  if (onClick) {
    return (
      <button
        type="button"
        aria-pressed={Boolean(active)}
        onClick={onClick}
        className={clsx("wa-kpi wa-kpi--button", `wa-kpi--${tone}`, active && "is-active")}
        title={active ? "Filter aufheben" : `Nur „${label}“ in der Objektliste anzeigen`}
        style={style}
      >
        {content}
      </button>
    );
  }
  return (
    <div className={clsx("wa-kpi", `wa-kpi--${tone}`)} style={style}>
      {content}
    </div>
  );
}

/* ───────────────────────── Notenverteilung ───────────────────────── */

export function GradeDistribution({ buckets, gradedAssets, ungradedDone }: { buckets: GradeBucket[]; gradedAssets: number; ungradedDone: number }) {
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <Panel
      title="Notenverteilung"
      hint={
        gradedAssets
          ? `${gradedAssets} bewertete Objekt${gradedAssets === 1 ? "" : "e"}${ungradedDone ? ` · ${ungradedDone} gewartet ohne Note` : ""}`
          : "Noch keine bewerteten Objekte"
      }
    >
      <div className="wa-grades" role="img" aria-label="Verteilung der Wartungsnoten">
        {buckets.map((bucket) => (
          <div key={bucket.grade} className="wa-grade" style={{ ["--wa-grade-color" as string]: getGradeColor(bucket.grade) }}>
            <span className="wa-grade__count">{bucket.count}</span>
            <div className="wa-grade__bar-area">
              <div
                className="wa-grade__bar"
                style={{ height: `${bucket.count ? Math.max(6, (bucket.count / max) * 100) : 2}%`, opacity: bucket.count ? 1 : 0.25 }}
                title={`Note ${bucket.grade}: ${bucket.count} (${bucket.percent} %)`}
              />
            </div>
            <span className="wa-grade__badge">{bucket.grade}</span>
            <span className="wa-grade__percent">{bucket.percent} %</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function GradePill({ grade }: { grade: number | null }) {
  if (grade == null) return <span className="wa-grade-pill wa-grade-pill--empty">–</span>;
  return (
    <span className="wa-grade-pill" style={{ ["--wa-grade-color" as string]: getGradeColor(grade) }} title={`Note ${grade}`}>
      {grade}
    </span>
  );
}

/* ───────────────────────── Mängel ───────────────────────── */

export function DefectsSection({
  groups,
  assetLabel,
  onOpenAsset,
}: {
  groups: DefectGroup[];
  assetLabel: (assetId: number) => string;
  onOpenAsset?: (assetId: number) => void;
}) {
  const total = groups.reduce((acc, g) => acc + g.entries.length, 0);
  return (
    <Panel
      title="Mängel"
      hint={total ? `${total} Mangel-Eintr${total === 1 ? "ag" : "äge"} in ${groups.length} Frage${groups.length === 1 ? "" : "n"}` : "Keine Mängel in den ausgewählten Fragen"}
    >
      {groups.length === 0 ? (
        <EmptyState compact title="Keine Mängel festgestellt" description="In den ausgewählten Mangel-Fragen wurden bei den gewarteten Objekten keine Mängel erfasst." />
      ) : (
        <div className="wa-defect-groups">
          {groups.map((group) => (
            <DefectGroupCard key={group.fieldKey} group={group} assetLabel={assetLabel} onOpenAsset={onOpenAsset} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function DefectGroupCard({
  group,
  assetLabel,
  onOpenAsset,
}: {
  group: DefectGroup;
  assetLabel: (assetId: number) => string;
  onOpenAsset?: (assetId: number) => void;
}) {
  const [expanded, setExpanded] = useState(group.entries.length <= 8);
  const shown = expanded ? group.entries : group.entries.slice(0, 8);
  return (
    <article className="wa-defect-group">
      <div className="wa-defect-group__head">
        <h4 className="wa-defect-group__title">{group.questionLabel}</h4>
        <span className="wa-defect-group__count">{group.entries.length} Objekt{group.entries.length === 1 ? "" : "e"}</span>
      </div>
      {group.answers.length > 1 || group.answers[0]?.label !== "Mangel" ? (
        <div className="wa-defect-answers">
          {group.answers.map((answer) => (
            <span key={answer.label} className="wa-chip" title={answer.gradeEffect != null ? `Notenwirkung: ${answer.gradeEffect}` : undefined}>
              {answer.label}
              <strong>{answer.count}</strong>
              {answer.gradeEffect != null ? <GradePill grade={answer.gradeEffect} /> : null}
            </span>
          ))}
        </div>
      ) : null}
      <ul className="wa-defect-list">
        {shown.map((entry, idx) => (
          <li key={`${entry.assetId}-${idx}`}>
            {onOpenAsset ? (
              <button type="button" className="wa-defect-list__asset hover:underline" onClick={() => onOpenAsset(entry.assetId)}>
                {assetLabel(entry.assetId)}
              </button>
            ) : (
              <span className="wa-defect-list__asset">{assetLabel(entry.assetId)}</span>
            )}
            <span className="wa-defect-list__answer">{entry.answerLabel}</span>
          </li>
        ))}
      </ul>
      {group.entries.length > 8 ? (
        <button type="button" className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-accent hover:underline" onClick={() => setExpanded((v) => !v)}>
          <ChevronDownIcon className={clsx("h-3.5 w-3.5 transition-transform", expanded && "rotate-180")} aria-hidden />
          {expanded ? "Weniger anzeigen" : `Alle ${group.entries.length} anzeigen`}
        </button>
      ) : null}
    </article>
  );
}

/* ───────────────────────── Material ───────────────────────── */

export function MaterialSection({
  aggregates,
  assetLabel,
}: {
  aggregates: MaterialAggregate[];
  assetLabel: (assetId: number) => string;
}) {
  const totalEntries = aggregates.reduce((acc, a) => acc + a.entries, 0);
  const columns: Column<MaterialAggregate>[] = [
    {
      key: "label",
      header: "Artikel",
      sortable: true,
      sortValue: (r) => r.label,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-label">{r.label}</div>
          {r.heroNr ? <div className="text-xs text-label-tertiary">Hero-Nr. {r.heroNr}</div> : null}
        </div>
      ),
    },
    {
      key: "qty",
      header: "Menge",
      sortable: true,
      sortValue: (r) => r.qtySum,
      className: "whitespace-nowrap tabular-nums",
      render: (r) => (
        <span>
          {r.qtySum > 0 ? `${formatNumber(r.qtySum)}${r.unit ? ` ${r.unit}` : ""}` : ""}
          {r.countWithoutQty > 0 ? (
            <span className="wa-table-cell-muted">{r.qtySum > 0 ? " + " : ""}{r.countWithoutQty}× ohne Menge</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "entries",
      header: "Erfassungen",
      sortable: true,
      sortValue: (r) => r.entries,
      className: "whitespace-nowrap tabular-nums",
      render: (r) => `${r.entries}`,
    },
    {
      key: "assets",
      header: "Objekte",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.assetIds.slice(0, 6).map((id) => (
            <span key={id} className="wa-chip">
              {assetLabel(id)}
            </span>
          ))}
          {r.assetIds.length > 6 ? <span className="wa-chip">+{r.assetIds.length - 6}</span> : null}
        </div>
      ),
    },
  ];
  return (
    <Panel
      title="Erfasstes Material"
      hint={totalEntries ? `${totalEntries} Materialposten · ${aggregates.length} Artikel` : "Kein Material in den ausgewählten Fragen"}
    >
      {aggregates.length === 0 ? (
        <EmptyState compact title="Kein Material erfasst" description="Bei den gewarteten Objekten wurde in den ausgewählten Material-Fragen nichts eingetragen." />
      ) : (
        <Table columns={columns} rows={aggregates} rowKey={(r) => r.groupKey} stickyHeader={false} />
      )}
    </Panel>
  );
}

/* ───────────────────────── Fragen-Auswertung ───────────────────────── */

export function QuestionBreakdownSection({
  breakdowns,
  assetLabel,
  onOpenSettings,
}: {
  breakdowns: QuestionBreakdown[];
  assetLabel: (assetId: number) => string;
  onOpenSettings: () => void;
}) {
  return (
    <Panel
      title="Auswertung nach Fragen"
      hint={breakdowns.length ? `${breakdowns.length} Frage${breakdowns.length === 1 ? "" : "n"} ausgewählt · Basis: gewartete Objekte` : "Keine Fragen ausgewählt"}
      actions={
        <button type="button" className="text-xs font-medium text-accent hover:underline" onClick={onOpenSettings}>
          Fragen anpassen
        </button>
      }
    >
      {breakdowns.length === 0 ? (
        <EmptyState
          compact
          title="Keine Fragen ausgewählt"
          description="Wähle über „Fragen anpassen“, welche Fragen des Formulars ausgewertet werden sollen."
        />
      ) : (
        <div className="wa-questions">
          {breakdowns.map((b) => (
            <QuestionCard key={b.question.key} breakdown={b} assetLabel={assetLabel} />
          ))}
        </div>
      )}
    </Panel>
  );
}

function roleClass(question: EvaluationQuestion): string {
  if (question.isDefect) return "wa-question__role--defect";
  if (question.isMaterial) return "wa-question__role--material";
  if (question.role === "measurement") return "wa-question__role--measurement";
  return "";
}

function QuestionCard({ breakdown, assetLabel }: { breakdown: QuestionBreakdown; assetLabel: (assetId: number) => string }) {
  const [openOption, setOpenOption] = useState<string | null>(null);
  const { question } = breakdown;
  const base = Math.max(1, breakdown.answered);
  return (
    <article className="wa-question">
      <div className="wa-question__head">
        <div className="min-w-0">
          <h4 className="wa-question__title">{question.label}</h4>
          <p className="wa-question__meta">
            {breakdown.answered} beantwortet{breakdown.unanswered ? ` · ${breakdown.unanswered} ohne Antwort` : ""}
          </p>
        </div>
        {question.roleLabel ? <span className={clsx("wa-question__role", roleClass(question))}>{question.roleLabel}</span> : null}
      </div>

      {(breakdown.kind === "options" || breakdown.kind === "bool") && (
        <div>
          {breakdown.options
            .filter((o) => o.count > 0)
            .map((opt) => {
              const expandable = opt.count > 0;
              const isOpen = openOption === opt.value;
              return (
                <div
                  key={opt.value}
                  className={clsx("wa-option", opt.isDefect && "wa-option--defect", expandable && "wa-option--expandable")}
                  role={expandable ? "button" : undefined}
                  tabIndex={expandable ? 0 : undefined}
                  onClick={expandable ? () => setOpenOption(isOpen ? null : opt.value) : undefined}
                  onKeyDown={
                    expandable
                      ? (e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setOpenOption(isOpen ? null : opt.value);
                          }
                        }
                      : undefined
                  }
                  style={opt.gradeEffect != null ? { ["--wa-option-color" as string]: getGradeColor(opt.gradeEffect) } : undefined}
                >
                  <span className="wa-option__label" title={opt.label}>
                    {opt.label}
                    {opt.gradeEffect != null ? <span className="ml-1.5 text-xs text-label-tertiary">→ Note {opt.gradeEffect}</span> : null}
                  </span>
                  <span className="wa-option__count">
                    {opt.count}
                    <span className="ml-1 text-xs font-normal text-label-tertiary">({breakdown.answered ? Math.round((opt.count / base) * 100) : 0} %)</span>
                  </span>
                  <div className="wa-option__track">
                    <div className="wa-option__fill" style={{ width: `${(opt.count / base) * 100}%` }} />
                  </div>
                  {isOpen ? (
                    <div className="wa-option__assets">
                      {opt.assetIds.map((id) => (
                        <span key={id} className="wa-chip">
                          {assetLabel(id)}
                        </span>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          {breakdown.answered === 0 ? <p className="wa-question__empty">Noch keine Antworten.</p> : null}
        </div>
      )}

      {breakdown.kind === "number" && (
        breakdown.numeric ? (
          <div className="wa-numeric">
            <div className="wa-numeric__item">
              <div className="wa-numeric__label">Min</div>
              <div className="wa-numeric__value">{formatNumber(breakdown.numeric.min)}</div>
            </div>
            <div className="wa-numeric__item">
              <div className="wa-numeric__label">Ø</div>
              <div className="wa-numeric__value">{formatNumber(breakdown.numeric.avg)}</div>
            </div>
            <div className="wa-numeric__item">
              <div className="wa-numeric__label">Max</div>
              <div className="wa-numeric__value">{formatNumber(breakdown.numeric.max)}</div>
            </div>
            <div className="wa-numeric__item">
              <div className="wa-numeric__label">Summe</div>
              <div className="wa-numeric__value">{formatNumber(breakdown.numeric.sum)}</div>
            </div>
          </div>
        ) : (
          <p className="text-xs text-label-tertiary">Keine Zahlenwerte erfasst.</p>
        )
      )}

      {(breakdown.kind === "text" || breakdown.kind === "material") && (
        breakdown.textAnswers.length ? (
          <TextAnswers answers={breakdown.textAnswers} assetLabel={assetLabel} />
        ) : (
          <p className="text-xs text-label-tertiary">Keine Einträge.</p>
        )
      )}
    </article>
  );
}

function TextAnswers({ answers, assetLabel }: { answers: { assetId: number; text: string }[]; assetLabel: (assetId: number) => string }) {
  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? answers : answers.slice(0, 5);
  return (
    <div>
      <ul className="wa-text-answers">
        {shown.map((a, idx) => (
          <li key={`${a.assetId}-${idx}`}>
            <span className="wa-text-answers__asset">{assetLabel(a.assetId)}</span>
            <span className="min-w-0 break-words">{a.text}</span>
          </li>
        ))}
      </ul>
      {answers.length > 5 ? (
        <button type="button" className="mt-2 text-xs font-medium text-accent hover:underline" onClick={() => setExpanded((v) => !v)}>
          {expanded ? "Weniger anzeigen" : `Alle ${answers.length} anzeigen`}
        </button>
      ) : null}
    </div>
  );
}

/* ───────────────────────── Objekt-Tabelle ───────────────────────── */

export function AssetEvaluationTable({
  rows,
  selectedQuestions,
  showAnswerColumns,
  emptyText,
  objectLabel,
  onOpenAsset,
}: {
  rows: AssetEvaluationRow[];
  selectedQuestions: EvaluationQuestion[];
  showAnswerColumns: boolean;
  emptyText: ReactNode;
  objectLabel: string;
  onOpenAsset?: (row: AssetEvaluationRow) => void;
}) {
  const columns: Column<AssetEvaluationRow>[] = [
    {
      key: "asset",
      header: objectLabel,
      sortable: true,
      sortValue: (r) => r.externalId || r.name,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium text-label">{r.externalId || r.name || `#${r.assetId}`}</div>
          {r.externalId && r.name && r.externalId !== r.name ? <div className="text-xs text-label-tertiary">{r.name}</div> : null}
        </div>
      ),
    },
    {
      key: "sub_location",
      header: "Bereich",
      sortable: true,
      sortValue: (r) => r.subLocationName,
      render: (r) => r.subLocationName || <span className="wa-table-cell-muted">–</span>,
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      sortValue: (r) => r.status,
      render: (r) => <span className={clsx("wa-status", `wa-status--${r.status}`)}>{EVALUATION_STATUS_LABELS[r.status]}</span>,
    },
    {
      key: "grade",
      header: "Note",
      sortable: true,
      sortValue: (r) => r.grade ?? 99,
      className: "whitespace-nowrap",
      render: (r) => (
        <span className="inline-flex items-center gap-1.5">
          <GradePill grade={r.grade} />
          {r.gradeManual != null && r.gradeAuto != null && r.gradeManual !== r.gradeAuto ? (
            <span className="text-xs text-label-tertiary" title={`Automatisch ermittelt: Note ${r.gradeAuto}`}>
              (auto {r.gradeAuto})
            </span>
          ) : null}
        </span>
      ),
    },
    {
      key: "defects",
      header: "Mängel",
      sortable: true,
      sortValue: (r) => r.defects.length,
      render: (r) =>
        r.defects.length ? (
          <ul className="wa-inline-list">
            {r.defects.map((d, idx) => (
              <li key={`${d.fieldKey}-${idx}`}>
                <span className="wa-inline-list__label">{d.questionLabel}: </span>
                <span className="text-label">{d.answerLabel}</span>
              </li>
            ))}
          </ul>
        ) : (
          <span className="wa-table-cell-muted">{r.isDone ? "keine" : "–"}</span>
        ),
    },
    {
      key: "material",
      header: "Material",
      sortable: true,
      sortValue: (r) => r.materials.length,
      render: (r) =>
        r.materials.length ? (
          <ul className="wa-inline-list">
            {r.materials.map((m, idx) => (
              <li key={`${m.fieldKey}-${idx}`} className="text-label">
                {[m.heroNr, m.label].filter(Boolean).join(" – ")}
                {m.qty != null ? <span className="wa-table-cell-muted"> · {formatNumber(m.qty)}{m.unit ? ` ${m.unit}` : ""}</span> : null}
              </li>
            ))}
          </ul>
        ) : (
          <span className="wa-table-cell-muted">{r.isDone ? "keins" : "–"}</span>
        ),
    },
    ...(showAnswerColumns
      ? selectedQuestions.map<Column<AssetEvaluationRow>>((q) => ({
          key: `q:${q.key}`,
          header: q.label,
          sortable: true,
          sortValue: (r) => r.answers[q.key] ?? "",
          render: (r) => r.answers[q.key] || <span className="wa-table-cell-muted">–</span>,
        }))
      : []),
    {
      key: "completed",
      header: "Abgeschlossen",
      sortable: true,
      sortValue: (r) => r.completedAt ?? "",
      className: "whitespace-nowrap",
      render: (r) => (
        <div>
          <div>{r.isDone ? formatDateTime(r.completedAt) : <span className="wa-table-cell-muted">–</span>}</div>
          {r.performedBy ? <div className="text-xs text-label-tertiary">{r.performedBy}</div> : null}
        </div>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={rows}
      rowKey={(r) => r.assetId}
      emptyText={emptyText}
      onRowClick={onOpenAsset}
      rowClassName={(r) => (r.hasIssue ? "wa-row--issue" : undefined)}
    />
  );
}
